import React, { useContext, useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { Star, Plus, Camera, Sparkles, Shirt } from "lucide-react";
import { AuthContext } from "../context/AuthContext";
import "../styles/vitrine.css";

// Le site vitrine : DressCode est une app iPhone, tout mène à l'App Store.
const APP_STORE = "https://apps.apple.com/fr/app/dresscode-outfit-planner/id6754649138";
const ASSISTANCE = "https://wassim-07305.github.io/dresscode-assistance/";
const CONFIDENTIALITE = "https://wassim-07305.github.io/dresscode-assistance/confidentialite.html";
const CONDITIONS = "https://www.apple.com/legal/internet-services/itunes/dev/stdeula/";
const CONTACT = "mailto:ahmanewassim6@gmail.com";

// Les captures viennent de la fiche App Store officielle (écrans réels de l'app).
const FONCTIONS = [
    {
        id: "garde-robe",
        titre: "Ta garde-robe, en photo",
        texte: "Prends tes vêtements en photo. L'app reconnaît la pièce, la couleur et la matière, détoure l'image et range tout. Trente pièces d'un coup si tu veux.",
        ecran: "/site/ecran-3.webp",
        alt: "Écran Garde-robe de DressCode : baskets, pantalons et sweats détourés, rangés par catégorie",
    },
    {
        id: "tenue-du-jour",
        titre: "Une tenue chaque matin",
        texte: "Composée avec ce que tu possèdes, pour la météo du jour et ce que tu as à faire. Quand tu ouvres l'app, la semaine est déjà prête.",
        ecran: "/site/ecran-1.webp",
        alt: "Écran d'accueil de DressCode : la semaine de tenues, la météo à Marseille et la tenue du lundi",
    },
    {
        id: "occasion",
        titre: "Une tenue pour chaque occasion",
        texte: "Un mariage, un entretien, un rendez-vous. Tu l'écris, la tenue arrive, avec tes vêtements à toi.",
        ecran: "/site/ecran-2.webp",
        alt: "Écran Générer de DressCode : champ libre, occasions Sortie, Bureau, Cérémonie et bouton Générer ma tenue",
    },
    {
        id: "composer",
        titre: "Ou compose à ta main",
        texte: "Choisis le haut, le pantalon, les chaussures. Change une pièce, garde les autres, enregistre ta tenue.",
        ecran: "/site/ecran-5.webp",
        alt: "Écran Composer de DressCode : un sweat, un pantalon et des baskets choisis",
    },
    {
        id: "pieces",
        titre: "Les pièces qui te manquent",
        texte: "Le styliste repère ce qui manque à ta garde-robe pour faire plus de tenues, et te montre où le trouver.",
        ecran: "/site/ecran-4.webp",
        alt: "Écran catalogue de DressCode : 900 pièces de marques comme Kith et Patta",
    },
    {
        id: "calendrier",
        titre: "Tout ce que tu as porté",
        texte: "Le calendrier garde chaque tenue portée. Tu vois ce qui sort souvent, ce qui dort au fond de l'armoire.",
        ecran: "/site/ecran-6.webp",
        alt: "Écran calendrier de DressCode : les tenues portées en septembre",
    },
];

const ETAPES = [
    {
        icone: Camera,
        titre: "Photographie tes pièces",
        texte: "Une photo par vêtement, ou trente à la suite. Le détourage et le rangement se font tout seuls.",
    },
    {
        icone: Sparkles,
        titre: "Reçois ta tenue",
        texte: "Chaque matin, ou pour l'occasion que tu écris. Toujours avec ce que tu as déjà.",
    },
    {
        icone: Shirt,
        titre: "Porte-la, fais-la noter",
        texte: "Une photo de toi, une note sur 100, et le conseil pour la porter encore mieux.",
    },
];

// Avis réels de l'App Store France, recopiés tels quels (flux RSS public Apple).
const AVIS = [
    {
        auteur: "James Drago",
        titre: "Excellente application",
        texte: "Super application très utile au quotidien elle me fait gagner du temps chaque matin et je m'habille beaucoup mieux qu'avant je recommande !",
    },
    {
        auteur: "Yassine_a12",
        titre: "L'application est incroyable",
        texte: "J'ai générer des tenues auxquelles j'avais jamais pensé au paravant !! Je recommande !",
    },
    {
        auteur: "Melissa_31_",
        titre: "Incroyable je recommande!",
        texte: "Hello, l'application est vraiment au top. Je l'utilise très souvent pour trouver de nouvelles idée de tenue. Je recommande vivement!",
    },
    {
        auteur: "Att2007",
        titre: "Très bien",
        texte: "Super application, ça m'aide au quotidien pour m'habiller",
    },
];

const QUESTIONS = [
    {
        q: "Où télécharger DressCode ?",
        r: "DressCode est une application iPhone, disponible sur l'App Store. L'abonnement commence par un essai gratuit de 3 jours, résiliable à tout moment depuis les réglages de ton compte Apple.",
    },
    {
        q: "Comment DressCode crée mes tenues ?",
        r: "L'app part des pièces de ta garde-robe, de la météo de ta ville et de ce que tu as à faire. Elle apprend aussi de ce que tu portes vraiment : plus tu l'utilises, plus les tenues te ressemblent.",
    },
    {
        q: "C'est pour les hommes ou pour les femmes ?",
        r: "Pour les deux. Tu choisis à l'inscription, et les tenues comme les pièces proposées suivent ton choix.",
    },
    {
        q: "Comment résilier ?",
        r: "L'abonnement est géré par Apple. Sur ton iPhone : Réglages, ton nom, Abonnements, DressCode, puis Annuler. Si tu annules pendant l'essai, tu ne paies rien.",
    },
    {
        q: "Comment DressCode est financé ?",
        r: "Par l'abonnement, et par des liens affiliés : quand tu achètes une pièce recommandée chez un marchand, nous pouvons toucher une commission, sans aucun surcoût pour toi. Les recommandations dépendent de ton style et de ta garde-robe.",
    },
    {
        q: "Que deviennent mes photos ?",
        r: "Elles servent à ranger ta garde-robe et à noter tes tenues, rien d'autre. Le détail est dans notre politique de confidentialité.",
        lien: { href: CONFIDENTIALITE, texte: "Lire la politique de confidentialité" },
    },
];

const BadgeAppStore = ({ clair = false, className = "" }) => (
    <a className={`dc-badge ${className}`} href={APP_STORE} aria-label="Télécharger DressCode dans l'App Store">
        <img
            src={clair ? "/site/badge-app-store-blanc.svg" : "/site/badge-app-store.svg"}
            alt="Télécharger dans l'App Store"
            width="152"
            height="48"
        />
    </a>
);

const Etoiles = ({ note = 5, taille = 16 }) => (
    <span className="dc-etoiles" aria-hidden="true">
        {[0, 1, 2, 3, 4].map((i) => (
            <Star key={i} size={taille} strokeWidth={0} fill={i < note ? "currentColor" : "#D9D9D9"} />
        ))}
    </span>
);

const Telephone = ({ src, alt, prioritaire = false, className = "" }) => (
    <div className={`dc-telephone ${className}`}>
        <img
            src={src}
            alt={alt}
            width="720"
            height="1560"
            loading={prioritaire ? "eager" : "lazy"}
            decoding="async"
            fetchpriority={prioritaire ? "high" : undefined}
        />
    </div>
);

// Les équerres du logo : le cadre de scan qui se referme sur l'écran.
const CadreScan = ({ children, className = "" }) => (
    <div className={`dc-cadre ${className}`}>
        <span className="dc-equerre dc-equerre--hg" aria-hidden="true" />
        <span className="dc-equerre dc-equerre--hd" aria-hidden="true" />
        <span className="dc-equerre dc-equerre--bg" aria-hidden="true" />
        <span className="dc-equerre dc-equerre--bd" aria-hidden="true" />
        {children}
    </div>
);

const EnTete = () => {
    const [defile, setDefile] = useState(false);

    useEffect(() => {
        // Sur iPhone, c'est #root qui défile (index.css), ailleurs la fenêtre.
        const racine = document.getElementById("root");
        const lire = () => setDefile((window.scrollY || (racine ? racine.scrollTop : 0)) > 8);
        window.addEventListener("scroll", lire, { passive: true });
        if (racine) racine.addEventListener("scroll", lire, { passive: true });
        return () => {
            window.removeEventListener("scroll", lire);
            if (racine) racine.removeEventListener("scroll", lire);
        };
    }, []);

    return (
        <header className={`dc-entete ${defile ? "dc-entete--defile" : ""}`}>
            <div className="dc-largeur dc-entete__ligne">
                <a href="#haut" className="dc-marque" aria-label="DressCode, retour en haut">
                    <img src="/Logo1.png" alt="" width="36" height="36" className="dc-marque__icone" />
                    <img src="/site/marque-mot.png" alt="DressCode" width="132" height="14" className="dc-marque__mot" />
                </a>
                <nav className="dc-nav" aria-label="Sections">
                    <a href="#fonctions">Ce qu'elle fait</a>
                    <a href="#styliste">Le styliste</a>
                    <a href="#avis">Avis</a>
                    <a href="#questions">Questions</a>
                </nav>
                <a href={APP_STORE} className="dc-bouton dc-bouton--petit">
                    Télécharger
                </a>
            </div>
        </header>
    );
};

const Hero = () => (
    <section className="dc-hero" id="haut">
        <div className="dc-largeur dc-hero__grille">
            <div className="dc-hero__texte">
                <h1 className="dc-hero__titre">
                    Tu ouvres ton armoire.
                    <span> DressCode te dit quoi mettre.</span>
                </h1>
                <p className="dc-hero__chapo">
                    Ton styliste personnel sur iPhone. Il range tes vêtements, compose tes tenues
                    selon la météo et ta journée, et note ton look sur 100.
                </p>
                <div className="dc-hero__actions">
                    <BadgeAppStore />
                    <p className="dc-hero__essai">Essai gratuit de 3 jours</p>
                </div>
                <a href="#avis" className="dc-note">
                    <Etoiles note={5} />
                    <span>
                        <strong>4,6 sur 5</strong> sur l'App Store, plus de 300 notes
                    </span>
                </a>
            </div>
            <div className="dc-hero__visuel">
                <CadreScan className="dc-cadre--hero">
                    <Telephone
                        src="/site/ecran-1.webp"
                        alt="L'écran d'accueil de DressCode : la tenue du lundi, un sweat, un pantalon marron et des baskets, avec la météo"
                        prioritaire
                    />
                </CadreScan>
            </div>
        </div>
    </section>
);

const Fonctions = () => {
    const [actif, setActif] = useState(0);
    const refs = useRef([]);

    useEffect(() => {
        if (!("IntersectionObserver" in window)) return undefined;
        const obs = new IntersectionObserver(
            (entrees) => {
                entrees.forEach((e) => {
                    if (e.isIntersecting) setActif(Number(e.target.dataset.index));
                });
            },
            { rootMargin: "-45% 0px -45% 0px" }
        );
        refs.current.forEach((el) => el && obs.observe(el));
        return () => obs.disconnect();
    }, []);

    return (
        <section className="dc-section dc-fonctions" id="fonctions" aria-labelledby="titre-fonctions">
            <div className="dc-largeur">
                <h2 className="dc-titre-section" id="titre-fonctions">
                    <span className="dc-ligne">Tes vêtements.</span>
                    <span className="dc-ligne">Tes tenues.</span>
                    <span className="dc-ligne">Chaque jour.</span>
                </h2>
                <div className="dc-fonctions__grille">
                    <ol className="dc-fonctions__liste">
                        {FONCTIONS.map((f, i) => (
                            <li
                                key={f.id}
                                ref={(el) => (refs.current[i] = el)}
                                data-index={i}
                                className={`dc-fonction ${actif === i ? "dc-fonction--active" : ""}`}
                            >
                                <div className="dc-fonction__texte">
                                    <span className="dc-fonction__compteur">
                                        {String(i + 1).padStart(2, "0")}
                                        <span>/{String(FONCTIONS.length).padStart(2, "0")}</span>
                                    </span>
                                    <h3>{f.titre}</h3>
                                    <p>{f.texte}</p>
                                </div>
                                <div className="dc-fonction__tuile">
                                    <Telephone src={f.ecran} alt={f.alt} />
                                </div>
                            </li>
                        ))}
                    </ol>
                    <div className="dc-fonctions__scene" aria-hidden="true">
                        <div className="dc-fonctions__collant">
                            <CadreScan className="dc-cadre--scene">
                                <div className="dc-telephone dc-telephone--pile">
                                    {FONCTIONS.map((f, i) => (
                                        <img
                                            key={f.id}
                                            src={f.ecran}
                                            alt=""
                                            width="720"
                                            height="1560"
                                            loading={i === 0 ? "eager" : "lazy"}
                                            decoding="async"
                                            className={actif === i ? "est-visible" : ""}
                                        />
                                    ))}
                                </div>
                            </CadreScan>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const Etapes = () => (
    <section className="dc-section dc-etapes" aria-labelledby="titre-etapes">
        <div className="dc-largeur">
            <h2 className="dc-titre-section" id="titre-etapes">
                Trois gestes, et c'est réglé.
            </h2>
            <ol className="dc-etapes__liste">
                {ETAPES.map((e, i) => {
                    const Icone = e.icone;
                    return (
                        <li key={e.titre} className="dc-etape">
                            <div className="dc-etape__haut">
                                <span className="dc-etape__numero">{i + 1}</span>
                                <Icone size={22} strokeWidth={1.75} aria-hidden="true" />
                            </div>
                            <h3>{e.titre}</h3>
                            <p>{e.texte}</p>
                        </li>
                    );
                })}
            </ol>
        </div>
    </section>
);

const Styliste = () => (
    <section className="dc-styliste" id="styliste" aria-labelledby="titre-styliste">
        <div className="dc-largeur">
            <div className="dc-styliste__tete">
                <h2 className="dc-styliste__titre" id="titre-styliste">
                    Montre ta tenue.
                    <br />
                    Le styliste te répond.
                </h2>
                <p className="dc-styliste__chapo">
                    Une photo de toi dans le miroir suffit. Tu reçois un verdict franc, sans jugement,
                    et de quoi faire mieux avec ce que tu as déjà.
                </p>
            </div>
            <div className="dc-styliste__note" aria-hidden="true">
                <span className="dc-styliste__sur">/100</span>
            </div>
            <ul className="dc-styliste__points">
                <li>
                    <h3>Une note sur 100</h3>
                    <p>Pour l'occasion que tu choisis : le quotidien, le bureau, un rendez-vous, une cérémonie.</p>
                </li>
                <li>
                    <h3>Les échanges possibles</h3>
                    <p>Une pièce de ta garde-robe qui irait mieux que celle que tu portes, et pourquoi.</p>
                </li>
                <li>
                    <h3>Comment la porter</h3>
                    <p>Rentrer le haut, retrousser le bas, changer de chaussures. Les détails qui changent tout.</p>
                </li>
            </ul>
            <div className="dc-styliste__achat">
                <p>
                    En magasin, une pièce te fait de l'œil ? Prends-la en photo :
                    le styliste te dit si elle va avec ta garde-robe, et avec quoi la porter.
                </p>
                <BadgeAppStore clair />
            </div>
        </div>
    </section>
);

const Avis = () => (
    <section className="dc-section dc-avis" id="avis" aria-labelledby="titre-avis">
        <div className="dc-largeur">
            <div className="dc-avis__tete">
                <h2 className="dc-titre-section" id="titre-avis">
                    Ils s'habillent avec.
                </h2>
                <div className="dc-avis__score">
                    <span className="dc-avis__chiffre">4,6</span>
                    <div>
                        <Etoiles note={5} taille={18} />
                        <p>sur 5, plus de 300 notes sur l'App Store en France</p>
                    </div>
                </div>
            </div>
            <ul className="dc-avis__liste">
                {AVIS.map((a) => (
                    <li key={a.auteur} className="dc-avis__carte">
                        <Etoiles note={5} taille={14} />
                        <p className="dc-avis__titre">{a.titre}</p>
                        <blockquote>{a.texte}</blockquote>
                        <p className="dc-avis__auteur">{a.auteur}, sur l'App Store</p>
                    </li>
                ))}
            </ul>
        </div>
    </section>
);

const Questions = () => (
    <section className="dc-section dc-questions" id="questions" aria-labelledby="titre-questions">
        <div className="dc-largeur dc-questions__grille">
            <h2 className="dc-titre-section" id="titre-questions">
                Questions fréquentes
            </h2>
            <div className="dc-questions__liste">
                {QUESTIONS.map((item) => (
                    <details key={item.q} className="dc-question">
                        <summary>
                            <span>{item.q}</span>
                            <Plus size={20} strokeWidth={1.75} aria-hidden="true" className="dc-question__plus" />
                        </summary>
                        <p>
                            {item.r}
                            {item.lien && (
                                <>
                                    {" "}
                                    <a href={item.lien.href}>{item.lien.texte}</a>
                                </>
                            )}
                        </p>
                    </details>
                ))}
            </div>
        </div>
    </section>
);

const Final = () => (
    <section className="dc-final" aria-labelledby="titre-final">
        <div className="dc-largeur dc-final__bloc">
            <img src="/site/marque-pictogramme.png" alt="" width="88" height="88" className="dc-final__picto" />
            <h2 id="titre-final">Demain matin, tu sais déjà quoi mettre.</h2>
            <BadgeAppStore />
            <p>Sur iPhone. Essai gratuit de 3 jours, sans engagement.</p>
        </div>
    </section>
);

const PiedDePage = () => (
    <footer className="dc-pied">
        <div className="dc-largeur">
            <div className="dc-pied__haut">
                <div className="dc-pied__marque">
                    <img src="/Logo1.png" alt="" width="40" height="40" />
                    <img src="/site/marque-mot.png" alt="DressCode" width="150" height="16" className="dc-pied__mot" />
                </div>
                <nav className="dc-pied__liens" aria-label="Liens utiles">
                    <a href={APP_STORE}>App Store</a>
                    <a href={ASSISTANCE}>Assistance</a>
                    <a href={CONTACT}>Contact</a>
                    <a href={CONFIDENTIALITE}>Confidentialité</a>
                    <a href={CONDITIONS}>Conditions d'utilisation</a>
                </nav>
            </div>
            <p className="dc-pied__affiliation">
                DressCode contient des liens affiliés : quand tu achètes une pièce chez un marchand
                depuis l'app, nous pouvons toucher une commission, sans aucun surcoût pour toi.
            </p>
            <p className="dc-pied__copyright">© 2026 DressCode. Tous droits réservés.</p>
        </div>
    </footer>
);

const Home = () => {
    const { currentUser } = useContext(AuthContext);

    // Les comptes de l'ancienne app web gardent leur accès au tableau de bord.
    if (currentUser) {
        return <Navigate to="/dashboard" />;
    }

    return (
        <div className="dc">
            <a href="#fonctions" className="dc-evitement">
                Aller au contenu
            </a>
            <EnTete />
            <main>
                <Hero />
                <Fonctions />
                <Etapes />
                <Styliste />
                <Avis />
                <Questions />
                <Final />
            </main>
            <PiedDePage />
        </div>
    );
};

export default Home;
